import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { hasPermission } from '@nwm-oss/auth-react/permissions';
import { resolveWorkspaceRuntime } from '@nwm-oss/auth-react/runtime';
import { useFrontendAuth } from '@nwm-oss/auth-react/session';
import { WorkspaceRuntimeBoundary } from '@nwm-oss/auth-react/workspace-runtime-boundary';
import { Breadcrumbs } from './Breadcrumbs';

type NavigationPermission = 'flow.access' | 'flow.operations.execute';

interface NavItem {
  to: string;
  label: string;
  group: 'Workspace' | 'Build' | 'Operate' | 'Administer' | 'AS2 Gateway';
  permission: NavigationPermission;
}

const navItems: NavItem[] = [
  { to: 'dashboard', label: 'Overview', group: 'Workspace', permission: 'flow.access' },
  { to: 'flows/builder', label: 'Set up integration', group: 'Build', permission: 'flow.operations.execute' },
  { to: 'schemas', label: 'Message schemas', group: 'Build', permission: 'flow.operations.execute' },
  { to: 'mapping-designer', label: 'Mapping designer', group: 'Build', permission: 'flow.operations.execute' },
  { to: 'test-lab', label: 'Test before production', group: 'Build', permission: 'flow.operations.execute' },
  { to: 'flows/publish', label: 'Publish center', group: 'Build', permission: 'flow.operations.execute' },
  { to: 'documents', label: 'Messages', group: 'Operate', permission: 'flow.access' },
  { to: 'executions', label: 'Executions', group: 'Operate', permission: 'flow.access' },
  { to: 'operations/command-center', label: 'Command center', group: 'Operate', permission: 'flow.access' },
  { to: 'dlq', label: 'Recovery queue', group: 'Operate', permission: 'flow.access' },
  { to: 'operations/maritime', label: 'Maritime', group: 'Operate', permission: 'flow.access' },
  { to: 'operations/cusdec-ml', label: 'DUM Mainlevée', group: 'Operate', permission: 'flow.access' },
  { to: 'operations/scanner', label: 'Scanner messages', group: 'Operate', permission: 'flow.access' },
  { to: 'operations/etat-chargement', label: 'Etat de Chargement', group: 'Operate', permission: 'flow.access' },
  { to: 'operations/tryptique', label: 'Tryptique', group: 'Operate', permission: 'flow.access' },
  { to: 'operations/amp', label: 'AMP (AMPI/AMPE)', group: 'Operate', permission: 'flow.access' },
  { to: 'operations/exchanges', label: 'Exchange log', group: 'Operate', permission: 'flow.access' },
  { to: 'flows', label: 'Flow catalog', group: 'Administer', permission: 'flow.operations.execute' },
  { to: 'connectors', label: 'Delivery channels', group: 'Administer', permission: 'flow.operations.execute' },
  { to: 'config/partners', label: 'Partners', group: 'Administer', permission: 'flow.operations.execute' },
  { to: 'mappings', label: 'Mapping records', group: 'Administer', permission: 'flow.operations.execute' },
  { to: 'templates', label: 'Templates', group: 'Administer', permission: 'flow.operations.execute' },
  { to: 'validation-rules', label: 'Validation rules', group: 'Administer', permission: 'flow.operations.execute' },
  { to: 'config/inbound-policies', label: 'Inbound policies', group: 'Administer', permission: 'flow.operations.execute' },
  { to: 'generate', label: 'Generate document', group: 'Administer', permission: 'flow.operations.execute' },
  { to: 'compose', label: 'Compose raw message', group: 'Administer', permission: 'flow.operations.execute' },
  { to: 'settings', label: 'Settings', group: 'Administer', permission: 'flow.operations.execute' },
  { to: 'as2', label: 'AS2 overview', group: 'AS2 Gateway', permission: 'flow.access' },
  { to: 'as2/outbound-messages', label: 'Outbound messages', group: 'AS2 Gateway', permission: 'flow.access' },
  { to: 'as2/inbound-messages', label: 'Inbound messages', group: 'AS2 Gateway', permission: 'flow.access' },
  { to: 'as2/dlq', label: 'Dead-letter queue', group: 'AS2 Gateway', permission: 'flow.access' },
  { to: 'as2/certificates', label: 'Certificates', group: 'AS2 Gateway', permission: 'flow.operations.execute' },
  { to: 'as2/transport-profiles', label: 'Transport profiles', group: 'AS2 Gateway', permission: 'flow.operations.execute' },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const { pathname, search } = useLocation();
  const { identity, isAuthenticated } = useFrontendAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const visibleNavItems = (isAuthenticated || Boolean(identity))
    ? navItems.filter((item) => hasPermission(identity?.permissions ?? [], item.permission))
    : [];
  const runtime = resolveWorkspaceRuntime({
    search,
    hostname: typeof window === 'undefined' ? 'localhost' : window.location.hostname,
  });

  useEffect(() => setMenuOpen(false), [pathname]);

  return (
    <WorkspaceRuntimeBoundary
      runtime={runtime.mode}
      standalone={(
        <div className="app-shell">
          <a className="skip-link" href="#main-content">Skip to main content</a>
          <aside className="sidebar">
            <div className="brand">
              <div><strong>OSS Integration</strong><span>Enterprise EAI console</span></div>
              <button className="nav-toggle" type="button" aria-expanded={menuOpen} aria-controls="primary-navigation" onClick={() => setMenuOpen((open) => !open)}>
                {menuOpen ? 'Close menu' : 'Menu'}
              </button>
            </div>
            <nav id="primary-navigation" className={`nav${menuOpen ? ' open' : ''}`} aria-label="Primary navigation">
              {Array.from(new Set(visibleNavItems.map((item) => item.group))).map((group) => (
                <div className="nav-group" key={group}>
                  <span>{group}</span>
                  {visibleNavItems.filter((item) => item.group === group).map((item) => (
                    <NavLink key={item.to} to={{ pathname: item.to, search }} end className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              ))}
            </nav>
          </aside>
          <main id="main-content" className="content" tabIndex={-1}>
            <Breadcrumbs />
            {children}
          </main>
        </div>
      )}
    >
      {children}
    </WorkspaceRuntimeBoundary>
  );
}
